import React, { useMemo } from "react";
import clsx from "clsx";
import { BsExclamationCircle, BsCurrencyRupee } from "react-icons/bs";
import { useUtils } from "@/app/hooks/useUtils";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";

export interface ISlotDetail {
  slot_id: string;
  start_time: string;
  end_time: string;
  configuration_id: string;
  prepaid_booking_slot?: string;
  booking_fee?: number;
  staff_id: string;
  staff_name: string;
}

export interface IDoctorSlots {
  staff_id: string;
  staff_name: string;
  slots: ISlotDetail[];
}

interface CmSlotsGridProps {
  formattedDate: string;
  doctorsSlots: IDoctorSlots[];
  selectedSlotId: string | null;
  onSelectSlot: (slot: ISlotDetail) => void;
  isLoading?: boolean;
}

export const CmSlotsGrid: React.FC<CmSlotsGridProps> = ({ formattedDate, doctorsSlots, selectedSlotId, onSelectSlot, isLoading }) => {
  const { addAMPMToTime } = useUtils();
  const { BASIC } = useImageConstants();

  const { isPaidbooking, isPaidPriceAmount } = useMemo(() => {
    const prepaidSlots = doctorsSlots
      .flatMap((d) => d.slots)
      .filter((s) => s.prepaid_booking_slot?.toLowerCase() === "yes");
    return {
      isPaidbooking: prepaidSlots.length > 0,
      isPaidPriceAmount: prepaidSlots[0]?.booking_fee ?? null,
    };
  }, [doctorsSlots]);

  const prepaidBanner = isPaidbooking ? (
    <div className="bg-[#FFF2F2] p-3 w-full flex gap-2 rounded-md my-2">
      <div className="mt-1">
        <BsExclamationCircle className="w-4 h-4 text-red-500 text-sm" />
      </div>
      <div>
        <p className="text-sm text-[#D01111] font-medium flex items-center gap-2">Advance Payment Required</p>
        <p className="text-sx text-[#D01111] flex">
          An advance payment of{" "}
          <span className="flex  text-xs items-center">
            <BsCurrencyRupee className="w-3 h-3" />
          </span>{" "}
          <span className="font-semibold"> {isPaidPriceAmount} </span>&nbsp; will be required for the prepaid
          slots
        </p>
      </div>
    </div>
  ) : null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-bold text-black font-sans">Clinic's Available Slots</h4>
          <span className="text-xs font-medium text-[#727272]">{formattedDate}</span>
        </div>
        {[0, 1].map((row) => (
          <div key={row} className="space-y-2.5">
            <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
            <div className="flex flex-wrap gap-[10px]">
              {Array.from({ length: 8 }).map((_, idx) => (
                <div
                  key={idx}
                  className="w-[90px] h-[30px] rounded-md bg-gray-200 animate-pulse"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (doctorsSlots.length === 0) {
    return (
      <div className="space-y-2">
        {prepaidBanner}
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-bold text-black font-sans">Clinic's Available Slots</h4>
          <span className="text-xs font-medium text-[#727272]">{formattedDate}</span>
        </div>
        <NoDataFoundComponent
          message="No slots available"
          imageURL={BASIC}
          description="No slots available for the selected date / service."
          className="!w-[80px] md:!w-[80px] xl:!w-[80px] 2xl:!w-[80px]"
          InputClassName="!my-4"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {prepaidBanner}
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-bold text-black font-sans">Clinic's Available Slots</h4>
        <span className="text-xs font-medium text-[#727272]">{formattedDate}</span>
      </div>
      {doctorsSlots.map((doc) => (
        <div key={doc.staff_id} className="space-y-2.5">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-bold text-black font-sans">{doc.staff_name}</h4>
          </div>

          <div className="flex flex-wrap gap-[10px]">
            {doc.slots.map((slot) => {
              const timeStr = addAMPMToTime(slot.start_time);
              const isSelected = selectedSlotId === slot.slot_id;
              const isPrepaid = slot.prepaid_booking_slot?.toLowerCase() === "yes";

              if (isPrepaid) {
                return (
                  <button
                    key={slot.slot_id}
                    type="button"
                    onClick={() => onSelectSlot(slot)}
                    className={clsx(
                      "w-[90px] border rounded-[6px] flex items-center shadow-sm focus:outline-none",
                      isSelected && "border-blue-500 bg-blue-100 text-blue-700",
                    )}
                    style={{
                      background: "linear-gradient(to top, #81B5FF 0%, #193A6A 100%)",
                    }}
                  >
                    <div
                      className={`w-full py-1.5 rounded-tl-2xl rounded-tr-md rounded-b-md text-[12px] text-center ${isSelected ? "bg-blue-50" : "bg-white"}`}
                    >
                      {timeStr}
                    </div>
                  </button>
                );
              }

              return (
                <button
                  key={slot.slot_id}
                  type="button"
                  onClick={() => onSelectSlot(slot)}
                  className={clsx(
                    "w-[90px] border rounded-md py-1.5 text-[12px] text-center shadow-sm focus:outline-none",
                    isSelected && "border-blue-500 bg-blue-200 text-blue-700",
                  )}
                >
                  {timeStr}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
